import { TrendingDown, TrendingUp, Minus, DollarSign, Package, BarChart3, Percent } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

/**
 * ScenarioExecutiveSummary - Executive summary banner with key KPIs
 * 
 * Shows at-a-glance comparison metrics:
 * - Net Cost Impact
 * - Volume Delta
 * - Revenue Delta
 * - Margin Impact
 * 
 * Features:
 * - Green gradient background for favorable, Red for unfavorable
 * - Large recommendation text
 * - Four mini KPI cards inline
 * - Annual savings/cost prominently displayed
 */
export default function ScenarioExecutiveSummary({ data, baselineScenario, comparisonScenario }) {
  if (!data || !data.summary) {
    return null;
  }

  const { summary } = data;
  const costDelta = summary.net_delta_cents || 0;
  const revenueDeltaDollars = summary.revenue_delta || 0;
  const volumeDelta = summary.volume_delta || 0;
  const annualSavings = summary.annual_savings_cents || 0;
  const hasCostReduction = costDelta < 0;
  const hasCostIncrease = costDelta > 0;
  const hasRevenueUpside = revenueDeltaDollars > 0;
  const hasRevenueDownside = revenueDeltaDollars < 0;
  const hasVolumeUpside = volumeDelta > 0;
  const hasVolumeDownside = volumeDelta < 0;

  const summaryTheme = (() => {
    if (hasCostReduction) {
      return {
        title: 'COST REDUCTION SCENARIO',
        text: `${comparisonScenario?.name || 'Comparison'} reduces costs${annualSavings > 0 ? ` by ${formatCompactCurrency(annualSavings)}/year` : ''}`,
        icon: TrendingDown,
        bg: 'bg-gradient-to-r from-green-50 to-emerald-50',
        badgeBg: 'bg-green-100',
        iconColor: 'text-green-600',
        titleColor: 'text-green-800',
        textColor: 'text-green-700'
      };
    }
    if (hasRevenueUpside && !hasCostIncrease) {
      return {
        title: 'REVENUE UPSIDE SCENARIO',
        text: `${comparisonScenario?.name || 'Comparison'} increases revenue by ${formatDollars(revenueDeltaDollars)} with no cost increase`,
        icon: TrendingUp,
        bg: 'bg-gradient-to-r from-green-50 to-emerald-50',
        badgeBg: 'bg-green-100',
        iconColor: 'text-green-600',
        titleColor: 'text-green-800',
        textColor: 'text-green-700'
      };
    }
    if (hasVolumeUpside && !hasCostIncrease) {
      return {
        title: 'VOLUME UPSIDE SCENARIO',
        text: `${comparisonScenario?.name || 'Comparison'} increases planned volume with no cost increase`,
        icon: TrendingUp,
        bg: 'bg-gradient-to-r from-green-50 to-emerald-50',
        badgeBg: 'bg-green-100',
        iconColor: 'text-green-600',
        titleColor: 'text-green-800',
        textColor: 'text-green-700'
      };
    }
    if (hasCostIncrease && hasRevenueUpside) {
      return {
        title: 'REVENUE UPSIDE WITH COST INCREASE',
        text: `${comparisonScenario?.name || 'Comparison'} increases revenue by ${formatDollars(revenueDeltaDollars)} and costs by ${formatCurrency(costDelta)}`,
        icon: TrendingUp,
        bg: 'bg-gradient-to-r from-amber-50 to-yellow-50',
        badgeBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        titleColor: 'text-amber-800',
        textColor: 'text-amber-700'
      };
    }
    if (hasCostIncrease) {
      return {
        title: 'COST INCREASE SCENARIO',
        text: `${comparisonScenario?.name || 'Comparison'} increases costs`,
        icon: TrendingUp,
        bg: 'bg-gradient-to-r from-red-50 to-rose-50',
        badgeBg: 'bg-red-100',
        iconColor: 'text-red-600',
        titleColor: 'text-red-800',
        textColor: 'text-red-700'
      };
    }
    if (hasRevenueDownside && !hasCostReduction) {
      return {
        title: 'REVENUE DOWNSIDE SCENARIO',
        text: `${comparisonScenario?.name || 'Comparison'} reduces revenue by ${formatDollars(revenueDeltaDollars)} with no cost change`,
        icon: TrendingDown,
        bg: 'bg-gradient-to-r from-red-50 to-rose-50',
        badgeBg: 'bg-red-100',
        iconColor: 'text-red-600',
        titleColor: 'text-red-800',
        textColor: 'text-red-700'
      };
    }
    if (hasVolumeDownside && !hasCostReduction) {
      return {
        title: 'VOLUME DOWNSIDE SCENARIO',
        text: `${comparisonScenario?.name || 'Comparison'} reduces planned volume with no cost change`,
        icon: TrendingDown,
        bg: 'bg-gradient-to-r from-red-50 to-rose-50',
        badgeBg: 'bg-red-100',
        iconColor: 'text-red-600',
        titleColor: 'text-red-800',
        textColor: 'text-red-700'
      };
    }
    return {
      title: 'NO MATERIAL CHANGE',
      text: `${comparisonScenario?.name || 'Comparison'} has no revenue or cost change`,
      icon: Minus,
      bg: 'bg-gradient-to-r from-slate-50 to-gray-50',
      badgeBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      titleColor: 'text-gray-800',
      textColor: 'text-gray-700'
    };
  })();
  const SummaryIcon = summaryTheme.icon;

  function formatCurrency(cents) {
    if (cents === null || cents === undefined) return 'N/A';
    const value = Math.abs(cents) / 100;
    const sign = cents < 0 ? '-' : cents > 0 ? '+' : '';
    if (value >= 1000000) return `${sign}$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${sign}$${(value / 1000).toFixed(1)}K`;
    return `${sign}$${value.toFixed(0)}`;
  }

  function formatCompactCurrency(cents) {
    if (cents === null || cents === undefined) return 'N/A';
    const value = Math.abs(cents) / 100;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  }

  function formatDollars(dollars) {
    if (dollars === null || dollars === undefined) return 'N/A';
    const value = Math.abs(dollars);
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  }

  function formatSignedDollars(dollars) {
    if (dollars === null || dollars === undefined) return 'N/A';
    const value = Math.abs(dollars);
    const sign = dollars > 0 ? '+' : dollars < 0 ? '-' : '';
    if (value >= 1000000) return `${sign}$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${sign}$${(value / 1000).toFixed(0)}K`;
    return `${sign}$${value.toFixed(0)}`;
  }

  function formatPercent(value) {
    if (value === null || value === undefined) return 'N/A';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  function formatNumber(num) {
    if (num === null || num === undefined) return 'N/A';
    const sign = num > 0 ? '+' : '';
    return `${sign}${num.toLocaleString('en-US')}`;
  }

  // KPI cards data
  const kpis = [
    {
      label: 'Cost Impact',
      value: formatCurrency(summary.net_delta_cents),
      subValue: formatPercent(summary.net_delta_percent),
      icon: DollarSign,
      isFavorable: summary.net_delta_cents < 0, // Lower cost is favorable
      isNeutral: summary.net_delta_cents === 0,
    },
    {
      label: 'Volume',
      value: formatNumber(summary.volume_delta),
      subValue: formatPercent(summary.volume_delta_percent),
      icon: Package,
      isFavorable: summary.volume_is_favorable,
    },
    {
      label: 'Revenue',
      value: formatSignedDollars(summary.revenue_delta),
      subValue: formatPercent(summary.revenue_delta_percent),
      icon: BarChart3,
      isFavorable: summary.revenue_is_favorable,
    },
    {
      label: 'Annual Cost Savings',
      value: formatCompactCurrency(Math.abs(summary.annual_savings_cents)),
      subValue: summary.annual_savings_cents < 0 ? 'Cost increase' : summary.annual_savings_cents > 0 ? 'per year' : 'No cost change',
      icon: Percent,
      isFavorable: summary.annual_savings_cents > 0,
      isNeutral: summary.annual_savings_cents === 0,
    },
  ];

  return (
    <Card className={`border-0 shadow-lg overflow-hidden ${summaryTheme.bg}`}>
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${summaryTheme.badgeBg}`}>
              <SummaryIcon className={`h-5 w-5 ${summaryTheme.iconColor}`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${summaryTheme.titleColor}`}>
                {summaryTheme.title}
              </h3>
              <p className={`text-sm ${summaryTheme.textColor}`}>
                {summaryTheme.text}
              </p>
            </div>
          </div>

          {/* Cost Impact Badge */}
          <div className={`px-4 py-2 rounded-lg ${summaryTheme.badgeBg}`}>
            <div className="text-right">
              <span className="text-xs font-medium text-gray-500 uppercase">Net Cost Impact</span>
              <p className={`text-2xl font-bold ${summaryTheme.textColor}`}>
                {formatCurrency(summary.net_delta_cents)}
              </p>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpis.map((kpi, index) => {
            const Icon = kpi.icon;
            return (
              <div 
                key={index}
                className={`p-4 rounded-lg border ${
                  kpi.isNeutral
                    ? 'bg-white border-gray-200'
                    : kpi.isFavorable
                    ? 'bg-white border-green-200' 
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`h-4 w-4 ${
                    kpi.isNeutral ? 'text-gray-500' : kpi.isFavorable ? 'text-green-600' : 'text-gray-500'
                  }`} />
                  <span className="text-xs font-medium text-gray-500 uppercase">{kpi.label}</span>
                </div>
                <p className={`text-xl font-bold ${
                  kpi.isNeutral ? 'text-gray-700' : kpi.isFavorable ? 'text-green-700' : 'text-gray-700'
                }`}>
                  {kpi.value}
                </p>
                <p className="text-xs text-gray-500">{kpi.subValue}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
